import React from "react";
import { Link, useNavigate } from "react-router-dom";
import "../styles/Home.css";
import heroImage from "../assets/hero-image.jpg";
import InstagramCarousel from "../components/InstagramCarousel";
import SEO from "../components/SEO";
import ServiceAreasHub from "../components/ServiceAreasHub";
import { BRAND_NAME, CONTACT } from "../data/contactInfo";
import { BRAND_TAGLINE, BRAND_HERO_SUBTITLE } from "../data/brandMessaging";
import { PAGE_SEO, getLocalBusinessSchema } from "../data/seoData";
import { SEO_IMAGES } from "../data/seoImages";

const aboutImage = `${process.env.PUBLIC_URL}/about.jpg`;
const guruImage = `${process.env.PUBLIC_URL}/vineesh.png`;

const Home = () => {
  const navigate = useNavigate();
  return (
    <div className="home-container">
      <SEO
        title={PAGE_SEO.home.title}
        description={PAGE_SEO.home.description}
        path={PAGE_SEO.home.path}
        ogImage={SEO_IMAGES.home}
        jsonLd={getLocalBusinessSchema()}
      />

      {/* Hero Section */}
      <div className="hero-section">
        <img src={heroImage} alt="Kerala Chenda Melam for wedding — Chennai performance" className="hero-image" />
        <div className="hero-content">
          <h1 className="hero-title">{BRAND_NAME}</h1>
          <h2 className="hero-tagline">{BRAND_TAGLINE}</h2>
          <p className="hero-subtitle">{BRAND_HERO_SUBTITLE}</p>
          <div className="hero-button-row">
            <button className="hero-button" onClick={() => navigate("/chenda-melam-chennai")}>Book for Wedding</button>
            <button className="hero-button hero-button-outline" onClick={() => navigate("/chenda-classes-chennai")}>Chenda Classes</button>
          </div>
        </div>
      </div>

      {/* About brand */}
      <div className="section-container">
        <img
          src={aboutImage}
          alt={`${BRAND_NAME} — Chenda Melam ensemble and students at ${CONTACT.address.area}, Chennai`}
          className="section-image"
          loading="lazy"
        />
        <div className="section-content">
          <h2 className="section-title">About {BRAND_NAME}</h2>
          <p className="section-text">
            <strong>{BRAND_NAME}</strong> brings authentic Kerala Chenda Melam to weddings and cultural events across Chennai and South India.
            From groom entry and baraat to reception and temple festivals, our specialists perform{" "}
            <Link to="/melams">Panchari Melam</Link> and <Link to="/programs">Panchavadyam</Link> with the energy and tradition your occasion deserves.
          </p>
          <p className="section-text">
            Under Guru <strong>Thiruvambady Vineesh Maarar</strong>, we also offer <Link to="/chenda-classes-chennai">Chenda Melam classes</Link> —
            online and at our {CONTACT.address.area} academy. <Link to="/chenda-melam-chennai">Book a performance</Link> or{" "}
            <Link to="/contact">message us on WhatsApp</Link> to get started.
          </p>
        </div>
      </div>

      {/* Why Join */}
      <div className="why-join-section">
        <div className="why-join-content">
          <h2 className="section-title">Why Join {BRAND_NAME}</h2>
          <div className="benefits-container">
            <div className="benefit-card">
              <div className="benefit-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 8h1a4 4 0 0 1 0 8h-1"></path>
                  <path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"></path>
                  <line x1="6" y1="1" x2="6" y2="4"></line>
                  <line x1="10" y1="1" x2="10" y2="4"></line>
                  <line x1="14" y1="1" x2="14" y2="4"></line>
                </svg>
              </div>
              <h3 className="benefit-title">Kerala Style Traditional</h3>
              <p className="benefit-text">
                Learn authentic Kerala style techniques — Veekku, Chaapu, Melam patterns, and Panchavadyam — from specialist gurus in the Marar and temple artist lineage.
              </p>
            </div>
            
            <div className="benefit-card">
              <div className="benefit-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <path d="M12 8v4l3 3"></path>
                </svg>
              </div>
              <h3 className="benefit-title">Flexible Schedule</h3>
              <p className="benefit-text">
                Weekend, evening, and flexible batches designed for students, working professionals, and children — learn Chenda, Thimila, Idakka, or Maddalam at your pace.
              </p>
            </div>
            
            <div className="benefit-card">
              <div className="benefit-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                </svg>
              </div>
              <h3 className="benefit-title">Chenda Melam Specialists</h3>
              <p className="benefit-text">
                Train under Thiruvambady Vineesh Maarar — Kerala percussion specialist with 30+ years in Melam, Panchavadyam, Sopana Sangeetham, and temple festival performances.
              </p>
            </div>
            
            <div className="benefit-card">
              <div className="benefit-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 12h-4l-3 9L9 3l-3 9H2"></path>
                </svg>
              </div>
              <h3 className="benefit-title">Performance Opportunities</h3>
              <p className="benefit-text">
                Regular arangam showcases, temple program participation, and festival performances at Kerala Samajam, temple programs, and cultural events across Chennai and Tamil Nadu.
              </p>
            </div>
          </div>
          <Link to="/courses" className="why-join-button">Explore Our Courses</Link>
        </div>
      </div>

      {/* What we offer */}
      <section className="home-services" aria-labelledby="home-services-heading">
        <div className="home-services-inner">
          <h2 id="home-services-heading" className="home-services-title">What We Offer</h2>
          <p className="home-services-intro">
            Whether you are planning a wedding or want to learn Kerala percussion, we are here to help.
          </p>
          <div className="home-services-grid">
            <article className="home-service-card">
              <span className="home-service-icon" aria-hidden="true">💛</span>
              <h3>Wedding Chenda Melam</h3>
              <p>Groom entry, baraat, reception, and muhurtham — performed by experienced Kerala artists.</p>
              <Link to="/chenda-melam-chennai" className="home-service-link">Book for your wedding</Link>
            </article>
            <article className="home-service-card">
              <span className="home-service-icon" aria-hidden="true">🎓</span>
              <h3>Chenda Melam Classes</h3>
              <p>Learn online from anywhere or join offline batches at our Chennai academy.</p>
              <Link to="/chenda-classes-chennai" className="home-service-link">View classes</Link>
            </article>
            <article className="home-service-card">
              <span className="home-service-icon" aria-hidden="true">🏛️</span>
              <h3>Temple &amp; Cultural Events</h3>
              <p>Panchari Melam, Panchavadyam, and festival programs for temples and celebrations.</p>
              <Link to="/programs" className="home-service-link">Explore programs</Link>
            </article>
            <article className="home-service-card">
              <span className="home-service-icon" aria-hidden="true">🥁</span>
              <h3>Chenda Melam Fusion</h3>
              <p>Melam with Mridangam, Parai, Kanjira and folk drums — for weddings and stage collaborations.</p>
              <Link to="/fusions" className="home-service-link">Explore fusions</Link>
            </article>
          </div>
          <p className="home-services-footnote">
            Based in Chennai · Serving Tamil Nadu, Andhra Pradesh &amp; Telangana.{" "}
            <Link to="/contact">Contact us</Link> for a quote.
          </p>
        </div>
      </section>

      <div className="home-areas-wrap">
        <ServiceAreasHub compact />
      </div>

      {/* About Guru Thiruvambady Vineesh Maarar */}
      <div className="section-container reverse guru-section">
        <div className="guru-image-container">
          <div className="guru-image-frame">
            <img src={guruImage} alt="Guru Thiruvambady Vineesh Maarar" className="guru-image" />
          </div>
          <div className="guru-badges">
            <span className="guru-badge">Kerala Style Traditional</span>
            <span className="guru-badge">Chenda Melam Specialist</span>
            <span className="guru-badge">30+ Years</span>
            <span className="guru-badge">Gurukul Tradition</span>
          </div>
        </div>
        <div className="section-content guru-content">
          <span className="guru-eyebrow">Guiding Master</span>
          <h2 className="section-title guru-title">About Our Guru</h2>
          <p className="guru-name">Thiruvambady Vineesh Maarar</p>
          <p className="section-text guru-lead">
            A distinguished Kerala percussion artist and renowned Idakka specialist with over 30 years
            of experience in temple music, Sopana Sangeetham, and Panchavadyam traditions.
          </p>
          <div className="guru-highlights">
            <div className="guru-highlight-card">
              <span className="guru-highlight-icon">🥁</span>
              <div>
                <h4>Idakka Mastery</h4>
                <p>Trained in the rigorous gurukul tradition — melodic sensitivity, precise talam, and devotional depth.</p>
              </div>
            </div>
            <div className="guru-highlight-card">
              <span className="guru-highlight-icon">🏛️</span>
              <div>
                <h4>Stage & Temple</h4>
                <p>Chenda Melam, Panchavadyam, and ensemble programs across Kerala and Tamil Nadu.</p>
              </div>
            </div>
            <div className="guru-highlight-card">
              <span className="guru-highlight-icon">🎓</span>
              <div>
                <h4>Teaching</h4>
                <p>Offline classes at the Sangam and online sessions for students across India and abroad.</p>
              </div>
            </div>
          </div>
          <blockquote className="guru-quote">
            "Whether beginner or advanced, his teaching emphasises authentic tradition, attentive listening,
            and the confidence to perform on temple stages and cultural platforms."
          </blockquote>
        </div>
      </div>

      <InstagramCarousel />

      {/* Know More About Instruments Section - Enhanced Version */}
      <div className="instruments-showcase-section">
        <div className="instruments-showcase-overlay"></div>
        <div className="instruments-showcase-content">
          <h2 className="instruments-showcase-title">Discover the Rhythmic Heritage of Kerala</h2>
          <p className="instruments-showcase-text">
            Immerse yourself in the rich tapestry of Kerala's percussion traditions. From the thunderous Chenda Melam
            of Thrissur Pooram to the melodic Idakka of Sopana Sangeetham, from the leading beats of Panchavadyam's
            Thimila to the sacred Mizhavu of Koodiyattam — nine instruments, centuries of heritage.
          </p>
          
          <div className="instruments-preview-container">
            <div className="instrument-preview">
              <div className="instrument-preview-circle">
                <span className="instrument-name-tag">Chenda</span>
              </div>
            </div>
            <div className="instrument-preview">
              <div className="instrument-preview-circle">
                <span className="instrument-name-tag">Maddalam</span>
              </div>
            </div>
            <div className="instrument-preview">
              <div className="instrument-preview-circle">
                <span className="instrument-name-tag">Idakka</span>
              </div>
            </div>
            <div className="instrument-preview">
              <div className="instrument-preview-circle">
                <span className="instrument-name-tag">Thimila</span>
              </div>
            </div>
            <div className="instrument-preview">
              <div className="instrument-preview-circle">
                <span className="instrument-name-tag">Kombu</span>
              </div>
            </div>
            <div className="instrument-preview">
              <div className="instrument-preview-circle">
                <span className="instrument-name-tag">Mizhavu</span>
              </div>
            </div>
          </div>
          
          <Link to="/instruments" className="instruments-showcase-button">
            <span>Explore Our Instruments</span>
            <svg className="arrow-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="5" y1="12" x2="19" y2="12"></line>
              <polyline points="12 5 19 12 12 19"></polyline>
            </svg>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Home;